"use client";

import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaCode, FaRobot, FaSync, FaChevronRight, FaDatabase, FaBolt, FaSearch, FaChartLine, FaBrain, FaEye, FaUsers, FaBox, FaShoppingCart, FaCog, FaMoneyBillWave, FaChartBar, FaTerminal, FaRocket, FaCloudUploadAlt, FaPlay, FaServer, FaHammer, FaShip, FaShieldAlt, FaMicrochip, FaTachometerAlt, FaHandPaper, FaBuilding } from "react-icons/fa";

const BusinessStructureMapping = () => {
  const items = [
    { title: "Company Setup", sub: "chart_of_accounts", color: "#F48428" },
    { title: "HR & Payroll", sub: "employees • salary", color: "#F48428" },
    { title: "Inventory & Stock", sub: "warehouse • items", color: "#F48428" },
    { title: "Accounting", sub: "ledgers • taxes", color: "#F48428" },
    { title: "Sales & Purchase", sub: "SO • PO • CRM", color: "#F48428" },
  ];

  const radarPills = [
    { label: "Company", icon: <FaDatabase />, color: "#F48428", top: "10%", right: "15%" },
    { label: "Sales", icon: <FaShoppingCart />, color: "#F48428", top: "30%", right: "5%" },
    { label: "Inventory", icon: <FaBox />, color: "#F48428", top: "50%", right: "5%" },
    { label: "HR", icon: <FaUsers />, color: "#F48428", top: "45%", left: "5%" },
    { label: "Mfg", icon: <FaCog />, color: "#F48428", top: "70%", left: "15%" },
    { label: "Accounts", icon: <FaMoneyBillWave />, color: "#F48428", bottom: "5%", left: "25%" },
  ];

  return (
    <div className="mapping-visualization w-100 h-100 p-2 d-flex gap-2 align-items-center" style={{ minHeight: "180px" }} aria-hidden="true">
      {/* Left List */}
      <div className="flex-grow-1 d-flex flex-column gap-2" style={{ maxWidth: "55%" }}>
        <div
          className="ps-opacity-pulse text-secondary font-monospace mb-1"
          style={{ fontSize: "0.5rem", opacity: 0.6 }}
        >
          {"// erp.module_scan() → "}<span className="text-orange">initializing</span>
        </div>
        {items.map((item, i) => (
          <div
            key={i}
            className="d-flex align-items-center p-2 px-3 cursor-pointer position-relative ps-list-item"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "8px",
              overflow: "hidden",
              transition: "all 0.3s ease"
            }}
          >
            {/* Minimal Straight Accent Bar */}
            <div style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "3px",
              background: item.color
            }} />
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-2 ps-scale-pulse"
              style={{ width: "12px", height: "12px", border: `1px solid ${item.color}`, color: item.color, animationDelay: `${i * 0.3}s` }}>
              <FaCheckCircle style={{ fontSize: "0.45rem" }} />
            </div>
            <div className="flex-grow-1">
              <div className="text-white fw-bold" style={{ fontSize: "0.6rem" }}>{item.title}</div>
            </div>
            <div className="text-white font-monospace d-none d-md-block" style={{ fontSize: "0.45rem", opacity: 0.8 }}>{item.sub}</div>

            {/* Dynamic Connection Beam */}
            <div
              className="ps-beam-sweep"
              style={{
                position: "absolute",
                right: "-20%",
                top: "50%",
                height: "1px",
                background: `linear-gradient(90deg, transparent, ${item.color}, transparent)`,
                zIndex: 1,
                pointerEvents: "none",
                animationDelay: `${i * 0.4}s`
              }}
            />
          </div>
        ))}
      </div>

      {/* Right Radar */}
      <div className="position-relative flex-grow-1 d-flex align-items-center justify-content-center h-100 overflow-hidden" style={{ width: "45%" }}>
        {/* Connection Lines Background */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.1, pointerEvents: "none" }}>
          {[...Array(5)].map((_, i) => (
            <line
              key={i}
              x1="0%" y1={`${20 + i * 15}%`}
              x2="50%" y2="50%"
              stroke="#F48428"
              strokeWidth="0.5"
              strokeDasharray="4 4"
              className="ps-dash-line"
            />
          ))}
        </svg>

        {/* Radar Circles */}
        {[...Array(3)].map((_, i) => (
          <div key={i} className="position-absolute top-50 start-50 translate-middle rounded-circle"
            style={{
              width: `${(i + 1) * 33}%`,
              height: `${(i + 1) * 33}%`,
              border: "0.5px solid rgba(255,255,255,0.08)",
              background: i === 0 ? "rgba(245, 131, 39, 0.02)" : "rgba(0,0,0,0)"
            }}
          />
        ))}

        {/* Enhanced Conic Scan Line */}
        <div
          className="position-absolute top-50 start-50 ps-spin-4s"
          style={{
            width: "200px",
            height: "200px",
            marginTop: "-100px",
            marginLeft: "-100px",
            background: "conic-gradient(from 0deg, transparent 0%, rgba(245, 131, 39, 0.2) 100%)",
            borderRadius: "50%",
            transformOrigin: "center",
            opacity: 0.5
          }}
        />

        {/* Orbiting Data Particles */}
        {[...Array(8)].map((_, i) => (
          <div
            key={`p-${i}`}
            className="position-absolute rounded-circle ps-orbit-particle"
            style={{
              width: "2px",
              height: "2px",
              background: "#F48428",
              boxShadow: "0 0 5px #F48428",
              left: "50%",
              top: "50%",
              '--dx': `${Math.cos(i) * 30}px`,
              '--dx-mid': `${Math.cos(i) * 60}px`,
              '--dy': `${Math.sin(i) * 30}px`,
              '--dy-mid': `${Math.sin(i) * 60}px`,
              animationDuration: `${3 + (i % 3)}s`,
              animationDelay: `${i * 0.5}s`
            }}
          />
        ))}

        {/* Pulsing Center */}
        <div
          className="position-absolute top-50 start-50 translate-middle rounded-circle ps-center-ping"
          style={{ width: "6px", height: "6px", background: "#F48428", boxShadow: "0 0 15px #F48428" }}
        />

        {/* Pills */}
        {radarPills.map((pill, i) => (
          <div
            key={i}
            className="position-absolute d-flex align-items-center gap-1 p-1 px-2 rounded-pill ps-radar-pill"
            style={{
              background: "rgba(0,0,0,0.9)",
              border: `0.5px solid ${pill.color}40`,
              top: pill.top,
              right: pill.right,
              left: pill.left,
              bottom: pill.bottom,
              zIndex: 2,
              fontSize: "0.4rem",
              backdropFilter: "blur(4px)",
              boxShadow: `0 0 10px ${pill.color}15`,
              animationDuration: `${3 + i}s`
            }}
          >
            <div style={{ color: pill.color }}>{pill.icon}</div>
            <span className="text-white fw-bold" style={{ fontSize: "0.4rem" }}>{pill.label}</span>
            <div
              className="rounded-circle ms-1 ps-dot-pulse"
              style={{ width: "3px", height: "3px", background: pill.color }}
            />
          </div>
        ))}

        {/* Floating Blips */}
        {[
          { top: "25%", left: "45%" },
          { top: "75%", left: "30%" },
          { top: "40%", left: "70%" },
          { top: "60%", left: "20%" },
        ].map((pos, i) => (
          <div
            key={`blip-${i}`}
            className="position-absolute rounded-circle ps-blip-pulse"
            style={{
              width: "4px",
              height: "4px",
              background: "#F48428",
              top: pos.top,
              left: pos.left,
              boxShadow: "0 0 8px #F48428",
              animationDelay: `${i * 0.7}s`
            }}
          />
        ))}
      </div>
    </div>
  );
};

const SmartAnalyzingVisual = () => {
  const checkItems = [
    { label: "Company Structure", sub: "chart_of_accounts", icon: <FaDatabase /> },
    { label: "HR & Payroll", sub: "employees • salary", icon: <FaUsers /> },
    { label: "Inventory & Stock", sub: "warehouse • items", icon: <FaBox /> },
    { label: "Accounting", sub: "ledgers • taxes", icon: <FaMoneyBillWave /> },
    { label: "Sales & Purchase", sub: "SO • PO • CRM", icon: <FaShoppingCart /> },
  ];

  return (
    <div className="smart-analyzing-container w-100 h-100 p-2 d-flex flex-row gap-2 gap-md-3 align-items-stretch" style={{ minHeight: "220px" }} aria-hidden="true">
      {/* Left Column: Radar Scanner */}
      <div className="radar-panel flex-grow-1 d-flex flex-column align-items-center justify-content-center p-3 position-relative"
        style={{
          width: "45%",
          background: "#050505",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: "12px",
          minHeight: "180px",
          overflow: "hidden"
        }}
      >
        <div className="radar-visual-wrapper position-absolute w-100 h-100 top-0 start-0 overflow-hidden d-flex align-items-center justify-content-center">
          {/* Radar concentric circles */}
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="position-absolute top-50 start-50 translate-middle rounded-circle"
              style={{
                width: `${(i + 1) * 36}px`,
                height: `${(i + 1) * 36}px`,
                border: "1px solid rgba(255, 255, 255, 0.2)",
                background: i === 0 ? "rgba(255, 255, 255, 0.02)" : "rgba(0,0,0,0)"
              }}
            />
          ))}

          {/* Conic scanning beam */}
          <div
            className="position-absolute top-50 start-50 ps-spin-4s"
            style={{
              width: "144px",
              height: "144px",
              marginTop: "-72px",
              marginLeft: "-72px",
              background: "conic-gradient(from 0deg, transparent 0%, rgba(255, 255, 255, 0.22) 100%)",
              borderRadius: "50%",
              transformOrigin: "center",
              opacity: 0.8
            }}
          />

          {/* Pulsing center dot */}
          <div
            className="position-absolute top-50 start-50 translate-middle rounded-circle ps-white-ping"
            style={{ width: "6px", height: "6px", background: "#ffffff", boxShadow: "0 0 10px rgba(255,255,255,0.8)" }}
          />

          {/* Floating blips/dots inside radar */}
          {[
            { top: "30%", left: "40%", delay: 0.5 },
            { top: "65%", left: "60%", delay: 1.2 },
            { top: "45%", left: "70%", delay: 1.8 }
          ].map((blip, i) => (
            <div
              key={i}
              className="position-absolute rounded-circle ps-blip-pulse"
              style={{
                width: "3px",
                height: "3px",
                background: "#ffffff",
                top: blip.top,
                left: blip.left,
                boxShadow: "0 0 8px #ffffff",
                animationDelay: `${blip.delay}s`
              }}
            />
          ))}
        </div>

        {/* Radar Label */}
        <div className="position-absolute bottom-2 start-50 translate-middle-x text-center w-100 mb-1" style={{ zIndex: 2 }}>
          <span
            className="font-monospace fw-bold ps-text-pulse"
            style={{ fontSize: "0.52rem", color: "#ffffff", letterSpacing: "0.3px" }}
          >
            Scanning ERPNext modules..
          </span>
        </div>
      </div>

      {/* Right Column: Checklist Items */}
      <div className="checklist-panel d-flex flex-column justify-content-between gap-2" style={{ width: "55%" }}>
        {checkItems.map((item, i) => (
          <div
            key={i}
            className="d-flex align-items-center gap-3 p-2 px-3 ps-checklist-item"
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              border: "1px solid rgba(255, 255, 255, 0.05)",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
            }}
          >
            <div className="d-flex align-items-center justify-content-center text-white" style={{ fontSize: "0.85rem", width: "20px" }}>
              {item.icon}
            </div>
            <div className="d-flex flex-column text-start">
              <span className="fw-semibold" style={{ fontSize: "0.68rem", color: "#dddddd", lineHeight: "1.2" }}>
                {item.label}
              </span>
              <span className="font-monospace" style={{ fontSize: "0.48rem", color: "#71717a", marginTop: "1px" }}>
                {item.sub}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DeployCodeVisual = () => {
  const [step, setStep] = useState(0);
  const [lines, setLines] = useState([]);
  const [progress, setProgress] = useState(0);
  const [stepRestartKey, setStepRestartKey] = useState(0);

  const deploymentLogs = [
    // Step 0: Bench Setup & App Retrieval
    [
      "$ bench get-app codeqlik_erpnext https://github.com/codeqlik/codeqlik_erpnext.git",
      "Cloning into 'apps/codeqlik_erpnext'...",
      "Installing custom codeqlik_erpnext dependencies...",
      "✔ Successfully retrieved custom ERPNext module."
    ],
    // Step 1: App Installation on Site
    [
      "$ bench --site erp.codeqlik.com install-app codeqlik_erpnext",
      "→ Connecting to MariaDB database 'erp_db_prod'...",
      "Installing app codeqlik_erpnext...",
      "✔ App codeqlik_erpnext successfully installed on site."
    ],
    // Step 2: Database Migrations
    [
      "$ bench --site erp.codeqlik.com migrate",
      "→ Executing database migrations & syncing DocTypes...",
      "Syncing: Sales Invoice, Stock Entry, HR Salary Structure...",
      "✔ Database schemas & custom fields migrated successfully."
    ],
    // Step 3: Asset Building
    [
      "$ bench build",
      "⚡ Compiling JS/CSS assets for bench...",
      "✔ Supervisor and Nginx configurations updated.",
      "✔ Production assets generated & cached successfully."
    ],
    // Step 4: Live & Restart Success
    [
      "$ bench restart",
      "→ Restarting Supervisor and Gunicorn worker processes...",
      "✔ Supervisor: all worker nodes active & healthy.",
      "🚀 Live URI: https://erp.codeqlik.com/jk-automobile",
      "✨ Status: ERPNext Production Stack LIVE (v15.2) 🛡"
    ]
  ];

  useEffect(() => {
    let lineIdx = 0;
    let stepIdx = 0;
    let logBuffer = [];

    setStep(0);
    setLines([]);
    setProgress(0);

    const interval = setInterval(() => {
      if (stepIdx < deploymentLogs.length) {
        setStep(stepIdx);
        const currentStepLogs = deploymentLogs[stepIdx];

        if (lineIdx < currentStepLogs.length) {
          logBuffer.push(currentStepLogs[lineIdx]);
          if (logBuffer.length > 5) {
            logBuffer.shift();
          }
          setLines([...logBuffer]);
          lineIdx++;

          if (stepIdx === 3) {
            setProgress((prev) => Math.min(prev + 25, 100));
          }
        } else {
          stepIdx++;
          lineIdx = 0;
          setProgress(0);
        }
      } else {
        clearInterval(interval);
        setTimeout(() => {
          setStepRestartKey((prev) => prev + 1);
        }, 5000);
      }
    }, 900);

    return () => clearInterval(interval);
  }, [stepRestartKey]);

  const stepsList = [
    { title: "Bench Init", icon: <FaHammer /> },
    { title: "App Install", icon: <FaBox /> },
    { title: "DB Migrate", icon: <FaDatabase /> },
    { title: "Asset Build", icon: <FaBolt /> },
    { title: "Gunicorn Live", icon: <FaServer /> }
  ];

  return (
    <div className="w-100 d-flex flex-column gap-2" style={{ minHeight: "155px", padding: "0 2px" }} aria-hidden="true">
      {/* Top Header: Live Bench Terminal Output */}
      <div
        className="d-flex flex-column justify-content-between p-2 rounded-2"
        style={{
          background: "#050505",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          height: "98px",
          overflow: "hidden"
        }}
      >
        <div className="d-flex align-items-center justify-content-between border-bottom border-white border-opacity-10 pb-1 mb-1">
          <div className="d-flex align-items-center gap-1.5">
            <span className="rounded-circle d-inline-block ps-dot-pulse" style={{ width: "5px", height: "5px", background: "#22c55e" }}></span>
            <span className="font-monospace text-secondary fw-bold" style={{ fontSize: "0.52rem", letterSpacing: "0.4px" }}>
              frappe-bench@cloud-instance: ~/frappe-bench
            </span>
          </div>
          <span className="badge bg-secondary bg-opacity-25 text-white font-monospace" style={{ fontSize: "0.45rem", padding: "2px 4px" }}>
            v15.2-RELEASE
          </span>
        </div>

        {/* Dynamic Log Output */}
        <div className="font-monospace d-flex flex-column gap-0.5" style={{ fontSize: "0.50rem", color: "#a1a1aa", overflow: "hidden" }}>
          {lines.map((line, idx) => (
            <div
              key={`${step}-${idx}-${line}`}
              className="d-flex gap-2 align-items-start ps-fade-in-left"
              style={{ lineHeight: "1.3" }}
            >
              <span className="text-muted" style={{ userSelect: "none" }}>›</span>
              <span style={{
                color: line.includes("✔") ? "#22c55e" : line.includes("🚀") || line.includes("✨") ? "#38bdf8" : line.includes("$") ? "#f48428" : "#cbd5e1",
                wordBreak: "break-all"
              }}>
                {line}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Pipeline Nodes Flow with Interactive Step Indicator */}
      <div className="d-flex align-items-center justify-content-between px-2 pt-1 position-relative">
        {stepsList.map((s, idx) => {
          const isDone = idx < step;
          const isCurrent = idx === step;
          return (
            <div key={idx} className="d-flex flex-column align-items-center gap-1 text-center position-relative" style={{ zIndex: 2 }}>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center position-relative"
                style={{
                  width: "22px",
                  height: "22px",
                  background: isDone ? "#22c55e" : isCurrent ? "var(--brand-color)" : "#18181b",
                  border: isCurrent ? "1.5px solid #fff" : "1px solid rgba(255, 255, 255, 0.15)",
                  color: isDone || isCurrent ? "#ffffff" : "#71717a",
                  transition: "all 0.3s ease"
                }}
              >
                {isCurrent && (
                  <div
                    className="position-absolute rounded-circle ps-spin-2s"
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "1.5px solid rgba(245, 131, 39, 0.3)",
                      borderTopColor: "#F48428",
                      inset: 0
                    }}
                  />
                )}
                {isDone ? (
                  <div className="d-flex align-items-center justify-content-center text-white ps-pop-in" style={{ fontSize: "0.55rem" }}>
                    <FaCheckCircle />
                  </div>
                ) : (
                  <div style={{ fontSize: "0.5rem" }}>{s.icon}</div>
                )}
              </div>
              <span className="font-monospace fw-semibold" style={{ fontSize: "0.45rem", color: isCurrent ? "#fff" : "#71717a" }}>
                {s.title}
              </span>
            </div>
          );
        })}

        {/* Dynamic Connected Track bar */}
        <div className="position-absolute top-30 start-0 w-100 px-4" style={{ zIndex: 1, transform: "translateY(2px)" }}>
          <div className="w-100" style={{ height: "2px", background: "rgba(255, 255, 255, 0.08)", borderRadius: "2px", overflow: "hidden" }}>
            <div
              className="h-100"
              style={{ background: "#d19a66", width: `${progress}%`, transition: "width 0.5s ease" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SeamlessIntegrationVisual = () => {
  const stacks = [
    { icon: <FaUsers />, label: "HR & Payroll", color: "#F48428" },
    { icon: <FaMoneyBillWave />, label: "Accounting", color: "#22c55e" },
    { icon: <FaBox />, label: "Stock/Inventory", color: "#61afef" },
    { icon: <FaShoppingCart />, label: "Sales & CRM", color: "#ef4444" },
    { icon: <FaRobot />, label: "Custom AI Mod", color: "#eab308" },
  ];

  const deploySteps = [
    { label: "Open Server Terminal", cmd: "ssh root@your-server-ip" },
    { label: "Deploy ERPNext Files", cmd: "bench get-app custom_erpnext_app" },
    { label: "Run Migration & Active", cmd: "bench --site erp.company.com migrate" },
    { label: "Launch Prod Server", cmd: "bench setup production frappe && bench restart" }
  ];

  const [activeStackIdx, setActiveStackIdx] = useState(0);
  const [activeStepIdx, setActiveStepIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStepIdx((prev) => (prev + 1) % deploySteps.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStackIdx((prev) => (prev + 1) % stacks.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const activeStack = (stacks && stacks[activeStackIdx]) || stacks[0] || { color: "#F48428", label: "HR & Payroll", icon: null };

  return (
    <div className="w-100 d-flex gap-2.5" style={{ minHeight: "145px", padding: "0 2px", gap: "10px" }} aria-hidden="true">
      {/* Left Column: ERP CORE Integration Pipeline Visual */}
      <div
        className="flex-grow-1 d-flex align-items-center justify-content-between px-3 position-relative overflow-hidden"
        style={{
          width: "50%",
          background: "#030303",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: "10px",
          padding: "12px",
          height: "145px"
        }}
      >
        {/* Subtle grid lines background */}
        <div className="position-absolute" style={{ inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.003) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.003) 1px, transparent 1px)", backgroundSize: "10px 10px", pointerEvents: "none" }} />

        {/* Connecting signal pulses */}
        <div className="position-absolute d-flex flex-column gap-1" style={{ left: "22%", right: "22%", top: "42%", transform: "translateY(-50%)", zIndex: 1 }}>
          {[...Array(2)].map((_, i) => (
            <div key={i} className="position-relative overflow-hidden" style={{ height: "1px", width: "100%", background: "rgba(255, 255, 255, 0.04)" }}>
              <div
                key={i}
                className="position-absolute top-0 bottom-0 ps-pulse-beam"
                style={{ width: "20px", background: `linear-gradient(90deg, transparent, ${activeStack.color}, transparent)`, animationDelay: `${i * 0.4}s` }}
              />
            </div>
          ))}
        </div>

        {/* Core Gateway Node */}
        <div className="d-flex flex-column align-items-center" style={{ zIndex: 2 }}>
          <div className="d-flex align-items-center justify-content-center" style={{ width: "42px", height: "42px", borderRadius: "8px", border: "1px solid rgba(255, 255, 255, 0.08)", background: "#08080a", boxShadow: "0 0 10px rgba(var(--brand-rgb), 0.1)" }}>
            <div className="ps-scale-pulse-building">
              <FaBuilding size={16} style={{ color: "var(--brand-color)", filter: "drop-shadow(0 0 4px rgba(var(--brand-rgb), 0.4))" }} />
            </div>
          </div>
          <span style={{ fontSize: "0.5rem", color: "#a1a1aa", marginTop: "4px", fontWeight: "700", letterSpacing: "0.4px" }}>ERP CORE</span>
        </div>

        {/* Dynamic Active Stack Node */}
        <div className="d-flex flex-column align-items-center" style={{ zIndex: 2 }}>
          <div
            className="d-flex align-items-center justify-content-center ps-stack-glow"
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "8px",
              border: `1px solid ${activeStack.color}45`,
              background: "#08080a",
              boxShadow: `0 0 8px ${activeStack.color}20`,
              transition: "all 0.4s ease"
            }}
          >
            <div
              key={activeStackIdx}
              style={{ color: activeStack.color, fontSize: "1.1rem" }}
              className="d-flex align-items-center justify-content-center ps-stack-icon-enter"
            >
              {activeStack.icon}
            </div>
          </div>
          <span style={{ fontSize: "0.5rem", color: "#a1a1aa", marginTop: "4px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.4px" }}>{activeStack.label}</span>
        </div>
      </div>

      {/* Right Column: Live Integration Steps Sequence */}
      <div
        className="flex-grow-1 d-flex flex-column justify-content-between p-2"
        style={{
          width: "50%",
          background: "#030303",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: "10px",
          height: "145px",
          gap: "4px"
        }}
      >
        {deploySteps.map((step, idx) => {
          const isActive = idx === activeStepIdx;
          return (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "2px 6px",
                borderRadius: "5px",
                fontSize: "0.52rem",
                transition: "all 0.3s ease",
                opacity: isActive ? 1 : 0.35,
                background: isActive ? "rgba(245, 131, 39, 0.06)" : "rgba(0,0,0,0)",
                border: isActive ? "1px solid rgba(245, 131, 39, 0.15)" : "1px solid transparent"
              }}
            >
              {/* Step indicator */}
              <div
                className="d-flex align-items-center justify-content-center rounded-circle font-monospace fw-bold"
                style={{
                  width: "12px",
                  height: "12px",
                  fontSize: "0.45rem",
                  background: isActive ? "var(--brand-color)" : "#1f1f23",
                  color: isActive ? "#fff" : "#71717a"
                }}
              >
                {idx + 1}
              </div>

              {/* Step Info */}
              <div className="flex-grow-1 d-flex justify-content-between align-items-center overflow-hidden">
                <span className="fw-semibold text-white" style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", maxWidth: "90px" }}>{step.label}</span>
                <span className="font-monospace" style={{ color: isActive ? "#F48428" : "#71717a", fontSize: "0.46rem", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>{step.cmd}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FeatureCard = ({ number, title, description, children, lightTheme = false }) => {
  return (
    <div
      className={`${lightTheme ? "premium-card-light" : "premium-card"} p-3 h-100 d-flex flex-column`}
      style={{ minHeight: "390px" }}
    >
      <div className="mb-3">
        <span className={lightTheme ? "step-badge-light mb-2 d-inline-block" : "step-badge mb-2 d-inline-block"}>
          Step {number}
        </span>
        <h3 className="text-white fw-bold mb-2 fs-5">{title}</h3>
        <p className="text-secondary mb-0" style={{ fontSize: "0.76rem", color: "#a1a1aa", lineHeight: "1.5" }}>
          {description}
        </p>
      </div>
      <div className="mt-auto flex-grow-1 d-flex flex-column justify-content-end">
        {children}
      </div>
    </div>
  );
};

const MiniTable = ({ title, columns, rows }) => (
  <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, margin: "6px 0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
      <span style={{ color: "#0f172a", fontSize: 10, fontWeight: 700 }}>{title}</span>
      <span style={{ color: "#ea580c", fontSize: 9, fontWeight: 600, background: "#ffedd5", padding: "1px 6px", borderRadius: 4 }}>Live Sync</span>
    </div>
    <div style={{ display: "flex", borderBottom: "1px solid #f1f5f9", paddingBottom: 4, marginBottom: 4 }}>
      {columns.map((c, i) => (
        <div key={i} style={{ flex: c.flex || 1, color: "#64748b", fontSize: 8, fontWeight: 600, textTransform: "uppercase" }}>{c.label}</div>
      ))}
    </div>
    {rows.map((r, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", padding: "3px 0", borderBottom: i !== rows.length - 1 ? "1px solid #f8fafc" : "none" }}>
        <div style={{ flex: columns[0]?.flex || 1, display: "flex", alignItems: "center", gap: 4 }}>
          {columns[0]?.key === "image" ? <div style={{ width: 14, height: 14, borderRadius: 3, background: "#e2e8f0" }} /> : null}
          <span style={{ color: "#1e293b", fontSize: 9, fontWeight: 500 }}>{r.name || r.id}</span>
        </div>
        {columns.slice(1).map((c, ci) => (
          <div key={ci} style={{ flex: c.flex || 1, color: "#64748b", fontSize: 9 }}>
            {c.key === "status" ? (
              <span style={{ color: "#16a34a", background: "#dcfce7", padding: "1px 4px", borderRadius: 3, fontSize: 8, fontWeight: 600 }}>Active</span>
            ) : c.key === "action" ? (
              <span style={{ color: "#2563eb", fontSize: 8, fontWeight: 600, cursor: "pointer" }}>Edit</span>
            ) : (
              r[c.key] || "-"
            )}
          </div>
        ))}
      </div>
    ))}
  </div>
);

const MiniDashboard = ({ dashboardValues }) => (
  <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, margin: "6px 0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
      <span style={{ color: "#0f172a", fontSize: 10, fontWeight: 700 }}>ERP Insights</span>
      <span style={{ color: "#64748b", fontSize: 8 }}>Real-time telemetry</span>
    </div>
    <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
      <div style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 6, padding: 8, background: "#f8fafc" }}>
        <div style={{ color: "#64748b", fontSize: 8 }}>Total Sales</div>
        <div style={{ color: "#0f172a", fontSize: 11, fontWeight: 700 }}>₹{dashboardValues[0]}</div>
      </div>
      <div style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 6, padding: 8, background: "#f8fafc" }}>
        <div style={{ color: "#64748b", fontSize: 8 }}>Active Invoices</div>
        <div style={{ color: "#0f172a", fontSize: 11, fontWeight: 700 }}>{dashboardValues[1]}</div>
      </div>
      <div style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 6, padding: 8, background: "#f8fafc" }}>
        <div style={{ color: "#64748b", fontSize: 8 }}>Inventory Items</div>
        <div style={{ color: "#0f172a", fontSize: 11, fontWeight: 700 }}>{dashboardValues[2]}</div>
      </div>
      <div style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 6, padding: 8, background: "#f8fafc" }}>
        <div style={{ color: "#64748b", fontSize: 8 }}>Active Employees</div>
        <div style={{ color: "#0f172a", fontSize: 11, fontWeight: 700 }}>{dashboardValues[3]}</div>
      </div>
    </div>
    <div style={{ display: "flex", gap: 6 }}>
      <div style={{ flex: 1.5, border: "1px solid #e2e8f0", borderRadius: 6, padding: 8, background: "#f8fafc" }}>
        <div style={{ color: "#0f172a", fontSize: 10, marginBottom: 6 }}>Monthly Revenue</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 45 }}>
          {[35, 55, 40, 70, 60, 85, 95].map((h, i) => (
            <div key={i} style={{ flex: 1, background: i === 6 ? "#ea580c" : "#cbd5e1", height: `${h}%`, borderRadius: "2px 2px 0 0" }} />
          ))}
        </div>
      </div>
      <div style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 6, padding: 8, background: "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ color: "#0f172a", fontSize: 10, marginBottom: 8, alignSelf: "flex-start" }}>Financial</div>
        <div style={{ width: 50, height: 50, borderRadius: "50%", border: "4px solid #e2e8f0", borderTopColor: "#3b82f6", borderRightColor: "#3b82f6", position: "relative" }}>
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#0f172a", fontWeight: 700 }}>₹0</div>
        </div>
      </div>
    </div>
  </div>
);

const MiniCatalog = () => (
  <MiniTable
    title="Product Catalog"
    columns={[
      { label: "Image", key: "image", flex: 0.5 },
      { label: "Name", key: "name", flex: 1.5 },
      { label: "Category", key: "cat", flex: 1 },
      { label: "Status", key: "status", flex: 1 },
      { label: "Action", key: "action", flex: 1 }
    ]}
    rows={[
      { name: "Brake Pads", cat: "Automotive" },
      { name: "BmwXz-223", cat: "Engine Parts" },
      { name: "Alternator", cat: "Electrical" },
    ]}
  />
);

const MiniUsers = () => (
  <MiniTable
    title="Admin Users"
    columns={[
      { label: "Full Name", key: "name", flex: 1 },
      { label: "Username", key: "user", flex: 1 },
      { label: "Email", key: "email", flex: 1.5 },
      { label: "Status", key: "status", flex: 1 },
      { label: "Action", key: "action", flex: 1 }
    ]}
    rows={[
      { name: "Harshita", user: "harshita.sharma", email: "harshita.sharma@codeqlik.com" },
      { name: "Jonathan Doe", user: "john.doe", email: "john.doe@codeqlik.com" },
      { name: "Arjun Kumar", user: "arjun.k", email: "arjun.kumar@codeqlik.com" },
    ]}
  />
);

const ContinuousOptimization = () => {
  const [dashboardValues, setDashboardValues] = useState([3036, 2012, 1450, 4233]);

  useEffect(() => {
    setDashboardValues([
      Math.floor(Math.random() * 5000) + 1000,
      Math.floor(Math.random() * 5000) + 1000,
      Math.floor(Math.random() * 5000) + 1000,
      Math.floor(Math.random() * 5000) + 1000,
    ]);
  }, []);

  return (
    <div style={{ background: "#ffffff", borderRadius: 12, overflow: "hidden", position: "relative", fontFamily: "Inter,sans-serif", border: "1px solid #e2e8f0" }}>
      {/* Browser chrome */}
      <div style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "8px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ display: "flex", gap: 5 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#ff5f57" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#febc2e" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#28c840" }} />
        </div>
        <div style={{ flex: 1, background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 10px", fontSize: 10, color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: "#ea580c", fontSize: 9 }}>🔒</span>
          <span style={{ color: "#0f172a" }}>erp-next.app/<span style={{ color: "#64748b" }}>jk-automobile-panel</span></span>
        </div>
        <div className="ps-dot-pulse" style={{ background: "#ffedd5", border: "1px solid #fed7aa", color: "#ea580c", fontSize: 9, padding: "3px 10px", borderRadius: 20, display: "flex", alignItems: "center", gap: 4, fontWeight: 600 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#ea580c" }} /> LIVE
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ width: "100%" }}>
            <div className="opt-scroll-container" style={{ height: 270, overflow: "hidden", position: "relative", background: "#f1f5f9", padding: "16px 12px" }}>
              <div
                className="ps-ticker-vertical"
                style={{ display: "flex", flexDirection: "column", width: "100%" }}
              >
                {/* Render the custom UI screens twice for seamless scrolling */}
                <MiniDashboard dashboardValues={dashboardValues} />
                <MiniCatalog />
                <MiniUsers />

                <MiniDashboard dashboardValues={dashboardValues} />
                <MiniCatalog />
                <MiniUsers />
              </div>
              {/* Overlay gradients for smooth edges */}
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "30px", background: "linear-gradient(to bottom, #f1f5f9, transparent)", zIndex: 2, pointerEvents: "none" }} />
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "30px", background: "linear-gradient(to top, #f1f5f9, transparent)", zIndex: 2, pointerEvents: "none" }} />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{ background: "#f8fafc", padding: "8px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div className="d-flex align-items-center gap-2">
          <div className="ps-dot-pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: "#ea580c" }} />
          <span style={{ color: "#475569", fontSize: 9, fontWeight: 500 }}>Live monitoring: <span style={{ color: "#0f172a", fontWeight: 600 }}>JK Automobile Panel</span></span>
        </div>
        <div className="d-flex gap-3 align-items-center" style={{ fontSize: 9, fontWeight: 600 }}>
          <span style={{ color: "#475569" }}>Status: <span style={{ color: "#ea580c" }}>Online</span></span>
          <span style={{ color: "#475569" }}>Uptime: <span style={{ color: "#ea580c" }}>99.9%</span></span>
        </div>
      </div>
    </div>
  );
};


const ProcessSection = () => {
  return (
    <section className="process-section py-5 position-relative overflow-hidden" style={{ fontFamily: "var(--font-inter), sans-serif" }}>
      <style jsx global>{`
        .process-section {
          --brand-orange: #F48428;
          --card-bg: rgba(255, 255, 255, 0.05);
          --card-border: rgba(255, 255, 255, 0.15);
        }

        .premium-card {
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 16px;
          backdrop-filter: blur(10px);
          transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          overflow: hidden;
        }

        .premium-card:hover {
          border-color: rgba(245, 131, 39, 0.4) !important;
        }

        .step-badge {
          background: rgba(245, 131, 39, 0.1) !important;
          color: #F48428 !important;
          padding: 4px 12px !important;
          border-radius: 100px !important;
          font-size: 0.65rem !important;
          font-weight: 800 !important;
          text-transform: uppercase !important;
          letter-spacing: 2px !important;
          border: 1px solid rgba(245, 131, 39, 0.2) !important;
        }

        .orange-glow {
          box-shadow: 0 0 30px rgba(245, 131, 39, 0.2);
        }
        .text-orange { color: var(--brand-orange); }

        .premium-card-light {
          background: #09090b !important;
          border: 1px solid rgba(255, 255, 255, 0.12) !important;
          border-radius: 16px !important;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
          transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          overflow: hidden;
        }

        .premium-card-light:hover {
          border-color: rgba(255, 255, 255, 0.35) !important;
          box-shadow: 0 10px 30px rgba(255, 255, 255, 0.06) !important;
        }

        .step-badge-light {
          background: rgba(245, 131, 39, 0.1) !important;
          color: #F48428 !important;
          padding: 4px 12px !important;
          border-radius: 100px !important;
          font-size: 0.65rem !important;
          font-weight: 800 !important;
          text-transform: uppercase !important;
          letter-spacing: 2px !important;
          border: 1px solid rgba(245, 131, 39, 0.2) !important;
        }

        /* Pure CSS keyframe animations for ProcessSection */
        @keyframes psSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes psOpacityPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }

        @keyframes psScalePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }

        @keyframes psBeamSweep {
          0% { width: 0%; opacity: 0; transform: translateX(0%); }
          50% { width: 100%; opacity: 0.4; }
          100% { width: 0%; opacity: 0; transform: translateX(100%); }
        }

        @keyframes psDashOffset {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -20; }
        }

        @keyframes psCenterPing {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
          50% { transform: translate(-50%, -50%) scale(2); opacity: 1; }
        }

        @keyframes psWhitePing {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          50% { transform: translate(-50%, -50%) scale(1.8); opacity: 1; }
        }

        @keyframes psFloatPill {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(2px, -4px); }
        }

        @keyframes psDotPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }

        @keyframes psBlipPulse {
          0%, 100% { opacity: 0; transform: scale(0.5); }
          50% { opacity: 1; transform: scale(1.5); }
        }

        @keyframes psFadeInLeft {
          0% { opacity: 0; transform: translateX(-5px); }
          100% { opacity: 1; transform: translateX(0); }
        }

        @keyframes psPopIn {
          0% { transform: scale(0.5) rotate(-20deg); }
          100% { transform: scale(1) rotate(0deg); }
        }

        @keyframes psPulseBeam {
          0% { left: -45%; }
          100% { left: 145%; }
        }

        @keyframes psScalePulseBuilding {
          0%, 100% { transform: scale(0.95); }
          50% { transform: scale(1.08); }
        }

        @keyframes psStackIconEnter {
          0% { opacity: 0; transform: scale(0.7); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes psTickerVertical {
          0% { transform: translateY(0%); }
          100% { transform: translateY(-50%); }
        }

        @keyframes psTickerCode {
          0% { transform: translateY(0); }
          100% { transform: translateY(-400px); }
        }

        @keyframes psOrbitParticle {
          0%, 100% { opacity: 0; transform: translate(var(--dx, 30px), var(--dy, 30px)) rotate(0deg); }
          50% { opacity: 1; transform: translate(var(--dx-mid, 60px), var(--dy-mid, 60px)) rotate(180deg); }
        }

        .ps-spin-4s { animation: psSpin 4s linear infinite; }
        .ps-spin-2s { animation: psSpin 2s linear infinite; }
        .ps-opacity-pulse { animation: psOpacityPulse 2s ease-in-out infinite; }
        .ps-scale-pulse { animation: psScalePulse 2s ease-in-out infinite; }
        .ps-beam-sweep { animation: psBeamSweep 2s linear infinite; }
        .ps-dash-line { animation: psDashOffset 2s linear infinite; }
        .ps-center-ping { animation: psCenterPing 2s ease-in-out infinite; }
        .ps-white-ping { animation: psWhitePing 2s ease-in-out infinite; }
        .ps-radar-pill { animation: psFloatPill 3s ease-in-out infinite; }
        .ps-dot-pulse { animation: psDotPulse 1.5s ease-in-out infinite; }
        .ps-blip-pulse { animation: psBlipPulse 3s ease-in-out infinite; }
        .ps-fade-in-left { animation: psFadeInLeft 0.2s ease forwards; }
        .ps-pop-in { animation: psPopIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        .ps-pulse-beam { animation: psPulseBeam 1.5s ease-in-out infinite; }
        .ps-scale-pulse-building { animation: psScalePulseBuilding 2s ease-in-out infinite; }
        .ps-stack-icon-enter { animation: psStackIconEnter 0.25s ease forwards; }
        .ps-ticker-vertical { animation: psTickerVertical 25s linear infinite; }
        .ps-ticker-code { animation: psTickerCode 25s linear infinite; }
        .ps-text-pulse { animation: psOpacityPulse 1.5s ease-in-out infinite; }
        .ps-orbit-particle { animation: psOrbitParticle 3s linear infinite; }

        .ps-checklist-item:hover {
          transform: translateX(4px);
          background: rgba(255, 255, 255, 0.08) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
        }

        /* Respect reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .ps-spin-4s,
          .ps-spin-2s,
          .ps-opacity-pulse,
          .ps-scale-pulse,
          .ps-beam-sweep,
          .ps-dash-line,
          .ps-center-ping,
          .ps-white-ping,
          .ps-radar-pill,
          .ps-dot-pulse,
          .ps-blip-pulse,
          .ps-fade-in-left,
          .ps-pop-in,
          .ps-pulse-beam,
          .ps-scale-pulse-building,
          .ps-stack-icon-enter,
          .ps-ticker-vertical,
          .ps-ticker-code,
          .ps-text-pulse,
          .ps-orbit-particle {
            animation: none !important;
            transform: none !important;
          }
        }

        /* Responsive adjustments for process section steps */
        @media (max-width: 991.98px) {
          .premium-card-light {
            min-height: 390px !important;
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            padding-bottom: 25px !important;
          }
        }

        @media (max-width: 767.98px) {
          .premium-card-light {
            min-height: 390px !important;
            height: 100% !important;
          }
          .smart-analyzing-container {
            flex-direction: row !important;
            gap: 8px !important;
          }
          .radar-panel {
            width: 42% !important;
            padding: 8px !important;
            min-height: 180px !important;
          }
          .radar-visual-wrapper {
            transform: scale(0.62) !important;
            transform-origin: center !important;
            overflow: visible !important;
          }
          .checklist-panel {
            width: 58% !important;
            gap: 6px !important;
          }
          .checklist-panel > div {
            padding: 6px 8px !important;
            gap: 8px !important;
          }
          .checklist-panel .font-monospace {
            display: none !important;
          }
          .editor-container {
            height: 200px !important;
          }
          .editor-container > div:last-child {
            height: calc(200px - 21px) !important;
          }
          .opt-parent-container {
            min-height: unset !important;
          }
          .opt-scroll-container {
            height: 155px !important;
          }
        }
      `}</style>

      <div className="container py-5 bg-black">
        {/* Header */}
        <div className="text-center mb-5">
          <div className="d-flex justify-content-center mb-3">
            <span className="step-badge">Our Process</span>
          </div>
          <h2 className="fw-bold text-white mb-4 brand-trust">
            Our Smart, Secure, and Scalable ERP Process
          </h2>
          <p
            className="text-secondary fs-8 mx-auto"
            style={{ maxWidth: "700px", lineHeight: "1.6" }}
          >
            We adhere to an agile and structured ERP implementation process, ensuring seamless deployment, security, and sustained business growth.
          </p>
        </div>

        {/* 4 Primary Phase Cards Matrix Layout */}
        <div className="process-matrix">
          {/* Row 1 */}
          <div className="row g-4 mb-3">
            {/* Step 1 */}
            <div className="col-lg-6">
              <div
                className="premium-card-light p-3 h-100 d-flex flex-column"
                style={{ minHeight: "390px" }}
              >
                <div className="mb-3">
                  <span className="step-badge-light mb-2 d-inline-block">Step 1</span>
                  <h3 className="text-white fw-bold mb-2 fs-5">Business Requirement & Architecture Mapping</h3>
                  <p className="text-secondary mb-0" style={{ fontSize: "0.76rem", color: "#a1a1aa", lineHeight: "1.5" }}>
                    We analyze your workflows, identify operational bottlenecks, and map out scalable ERP architecture tailored to your business model.
                  </p>
                </div>

                <div className="mt-auto flex-grow-1 d-flex flex-column justify-content-end">
                  <SmartAnalyzingVisual />
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="col-lg-6">
              <div
                className="premium-card-light p-3 h-100 d-flex flex-column"
                style={{ minHeight: "390px" }}
              >
                <div className="mb-3">
                  <span className="step-badge-light mb-2 d-inline-block">Step 2</span>
                  <h3 className="text-white fw-bold mb-2 fs-5">Custom Development & Module Integration</h3>
                  <p className="text-secondary mb-0" style={{ fontSize: "0.76rem", color: "#a1a1aa", lineHeight: "1.5" }}>
                    Our engineers develop custom business logic, automated workflows, and intelligent reporting systems.
                  </p>
                </div>

                <div className="editor-container mt-auto overflow-hidden" style={{ border: "1px solid rgba(225, 86, 86, 0.05)", height: "310px", borderRadius: "12px" }}>
                  <div className="bg-dark p-1 px-2 d-flex align-items-center gap-1" style={{ background: "#111 !important" }}>
                    <div className="rounded-circle bg-danger" style={{ width: "4px", height: "4px", opacity: 0.6 }}></div>
                    <div className="rounded-circle bg-warning" style={{ width: "4px", height: "4px", opacity: 0.6 }}></div>
                    <div className="rounded-circle bg-success" style={{ width: "4px", height: "4px", opacity: 0.6 }}></div>
                  </div>
                  <div className="bg-black p-2 position-relative" style={{ height: "calc(310px - 21px)", overflow: "hidden" }}>
                    <div
                      className="d-flex gap-2 font-monospace ps-ticker-code"
                      style={{ fontSize: "0.65rem", color: "#abb2bf" }}
                    >
                      <div className="text-secondary opacity-30 border-end border-white border-opacity-5 pe-2 d-none d-md-block">
                        {Array.from({ length: 32 }, (_, i) => i + 1).map(n => <div key={n}>{n}</div>)}
                      </div>
                      <div>
                        <pre style={{ margin: 0, color: "#abb2bf" }}>
                          <span style={{ color: "#c678dd" }}>def</span> <span style={{ color: "#61afef" }}>__init__</span>(self, threshold):<br />
                          &nbsp;&nbsp;self.threshold = threshold<br />
                          &nbsp;&nbsp;self.status = <span style={{ color: "#98c379" }}>"inactive"</span><br />
                          &nbsp;&nbsp;self.history = []<br /><br />
                          <span style={{ color: "#c678dd" }}>def</span> <span style={{ color: "#61afef" }}>check_trigger</span>(self, value):<br />
                          &nbsp;&nbsp;self.history.append(value)<br />
                          &nbsp;&nbsp;<span style={{ color: "#c678dd" }}>if</span> value {">"} self.threshold:<br />
                          &nbsp;&nbsp;&nbsp;&nbsp;self.status = <span style={{ color: "#98c379" }}>"active"</span><br />
                          &nbsp;&nbsp;&nbsp;&nbsp;self.process_alert(value)<br /><br />
                          <span style={{ color: "#c678dd" }}>def</span> <span style={{ color: "#61afef" }}>process_alert</span>(self, val):<br />
                          &nbsp;&nbsp;<span style={{ color: "#e06c75" }}>print</span>(<span style={{ color: "#98c379" }}>f"ALRT: &#123;val&#125;"</span>)<br />
                          &nbsp;&nbsp;self.sync_to_cloud()<br /><br />
                          <span style={{ color: "#c678dd" }}>def</span> <span style={{ color: "#61afef" }}>sync_to_cloud</span>(self):<br />
                          &nbsp;&nbsp;<span style={{ color: "#5c6370" }}># Initializing handshake</span><br />
                          &nbsp;&nbsp;<span style={{ color: "#c678dd" }}>return</span> <span style={{ color: "#d19a66" }}>True</span><br /><br />
                          <span style={{ color: "#c678dd" }}>def</span> <span style={{ color: "#61afef" }}>reset_state</span>(self):<br />
                          &nbsp;&nbsp;self.status = <span style={{ color: "#98c379" }}>"inactive"</span><br />
                          &nbsp;&nbsp;self.history.clear()<br />
                          &nbsp;&nbsp;<span style={{ color: "#5c6370" }}># Core logic reset complete</span><br />
                          &nbsp;&nbsp;<span style={{ color: "#c678dd" }}>return</span> <span style={{ color: "#d19a66" }}>self.status</span><br /><br />
                          <span style={{ color: "#c678dd" }}>def</span> <span style={{ color: "#61afef" }}>generate_report</span>(self):<br />
                          &nbsp;&nbsp;data = self.fetch_analytics()<br />
                          &nbsp;&nbsp;processed = <span style={{ color: "#e06c75" }}>map</span>(self.format, data)<br />
                          &nbsp;&nbsp;<span style={{ color: "#c678dd" }}>return</span> <span style={{ color: "#e06c75" }}>list</span>(processed)<br /><br />
                          <span style={{ color: "#c678dd" }}>def</span> <span style={{ color: "#61afef" }}>fetch_analytics</span>(self):<br />
                          &nbsp;&nbsp;<span style={{ color: "#c678dd" }}>pass</span>
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="row g-4 mt-1">
            {/* Step 3 */}
            <div className="col-lg-6">
              <div
                className="premium-card-light p-3 h-100 d-flex flex-column"
                style={{ minHeight: "390px" }}
              >
                <div className="mb-3">
                  <span className="step-badge-light mb-2 d-inline-block">Step 3</span>
                  <h3 className="text-white fw-bold mb-2 fs-5" style={{ letterSpacing: "-0.02em" }}>Automated ERP Go-Live & Launch</h3>
                  <p className="text-secondary mb-0" style={{ fontSize: "0.76rem", color: "#a1a1aa", lineHeight: "1.5" }}>
                    We compile your production assets, migrate custom database DocTypes, and launch your stable live ERPNext project with zero downtime.
                  </p>
                </div>

                <div className="integration-box p-2 mt-2 d-flex flex-column gap-2 align-items-stretch" style={{ background: "#000000", border: "1.2px solid rgba(255, 255, 255, 0.12)", overflow: "hidden", borderRadius: "12px" }}>
                  {/* Top: Deploy Code Terminal Animation */}
                  <DeployCodeVisual />

                  {/* Subtle Divider line */}
                  <div style={{ height: "1px", width: "100%", background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent)" }} />

                  {/* Bottom: Connection pipeline visual with dynamic tech stack */}
                  <SeamlessIntegrationVisual />
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="col-lg-6">
              <div
                className="premium-card-light p-3 h-100 d-flex flex-column"
                style={{ minHeight: "390px" }}
              >
                <div className="mb-3">
                  <span className="step-badge-light mb-2 d-inline-block">Step 4</span>
                  <h3 className="text-white fw-bold mb-2 fs-5">Real-time Process Optimization</h3>
                  <p className="text-secondary mb-0" style={{ fontSize: "0.76rem", color: "#a1a1aa", lineHeight: "1.5" }}>
                    We continuously monitor and refine your business operations to maximize efficiency and ROI.
                  </p>
                </div>

                <div className="opt-parent-container mt-auto flex-grow-1 overflow-hidden" style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.03)", minHeight: "270px", borderRadius: "12px" }}>
                  <ContinuousOptimization />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
